/**
 * Faculty Entity
 *
 * Aggregate root for faculty records.
 */
import { z } from 'zod';
import {
  FacultyId,
  OrgId,
  UserId,
  ResourceId,
  UnixTimestamp,
  DivisionId,
} from '../types/branded';
import { FacultyStatusSchema } from '../types/enums';

export const FacultyGenderSchema = z.enum(['male', 'female']);
export type FacultyGender = z.infer<typeof FacultyGenderSchema>;

export const FacultyEmploymentTypeSchema = z.enum(['full_time', 'part_time']);
export type FacultyEmploymentType = z.infer<typeof FacultyEmploymentTypeSchema>;

export const FacultyAddressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
});
export type FacultyAddress = z.infer<typeof FacultyAddressSchema>;

export const FacultySchema = z.object({
  id: FacultyId,
  orgId: OrgId,
  status: FacultyStatusSchema,
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  displayName: z.string().min(1).optional(),
  middleName: z.string().optional(),
  preferredName: z.string().optional(),
  hebrewFirstName: z.string().optional(),
  hebrewLastName: z.string().optional(),
  dateOfBirth: UnixTimestamp.nullable().optional(),
  gender: FacultyGenderSchema.nullable().optional(),
  profilePhotoUrl: z.string().optional(),
  email: z.string().email().nullable().optional(),
  personalEmail: z.string().email().nullable().optional(),
  phone: z.string().min(7).nullable().optional(),
  homePhone: z.string().nullable().optional(),
  address: FacultyAddressSchema.nullable().optional(),
  employeeId: z.string().nullable().optional(),
  hireDate: UnixTimestamp.nullable().optional(),
  position: z.string().optional(),
  employmentType: FacultyEmploymentTypeSchema.nullable().optional(),
  divisions: z.array(z.string().min(1)).optional(),
  gradeLevels: z.array(z.string().min(1)).optional(),
  subjectLabels: z.array(z.string().min(1)).optional(),
  homeroom: z.string().nullable().optional(),
  reportsTo: z.string().nullable().optional(),
  tags: z.array(z.string().min(1)).optional(),
  userId: UserId.nullable().optional(),
  externalId: z.string().min(1).nullable().optional(),
  resourceId: ResourceId.nullable().optional(),
  primaryDivisionId: DivisionId.nullable().optional(),
  invitedAt: UnixTimestamp.nullable().optional(),
  activatedAt: UnixTimestamp.nullable().optional(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
});
export type Faculty = z.infer<typeof FacultySchema>;

export const CreateFacultyInputSchema = z.object({
  orgId: OrgId,
  status: FacultyStatusSchema.default('invited'),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  displayName: z.string().min(1).optional(),
  middleName: z.string().optional(),
  preferredName: z.string().optional(),
  hebrewFirstName: z.string().optional(),
  hebrewLastName: z.string().optional(),
  dateOfBirth: UnixTimestamp.nullable().optional(),
  gender: FacultyGenderSchema.nullable().optional(),
  profilePhotoUrl: z.string().optional(),
  email: z.string().email().nullable().optional(),
  personalEmail: z.string().email().nullable().optional(),
  phone: z.string().min(7).nullable().optional(),
  homePhone: z.string().nullable().optional(),
  address: FacultyAddressSchema.nullable().optional(),
  employeeId: z.string().nullable().optional(),
  hireDate: UnixTimestamp.nullable().optional(),
  position: z.string().optional(),
  employmentType: FacultyEmploymentTypeSchema.nullable().optional(),
  divisions: z.array(z.string().min(1)).optional(),
  gradeLevels: z.array(z.string().min(1)).optional(),
  subjectLabels: z.array(z.string().min(1)).optional(),
  homeroom: z.string().nullable().optional(),
  reportsTo: z.string().nullable().optional(),
  tags: z.array(z.string().min(1)).optional(),
  userId: UserId.nullable().optional(),
  externalId: z.string().min(1).nullable().optional(),
  resourceId: ResourceId.nullable().optional(),
  primaryDivisionId: DivisionId.nullable().optional(),
});
export type CreateFacultyInput = z.infer<typeof CreateFacultyInputSchema>;

export const UpdateFacultyInputSchema = z.object({
  facultyId: FacultyId,
  status: FacultyStatusSchema.optional(),
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  displayName: z.string().min(1).optional(),
  middleName: z.string().optional(),
  preferredName: z.string().optional(),
  hebrewFirstName: z.string().optional(),
  hebrewLastName: z.string().optional(),
  dateOfBirth: UnixTimestamp.nullable().optional(),
  gender: FacultyGenderSchema.nullable().optional(),
  profilePhotoUrl: z.string().optional(),
  email: z.string().email().nullable().optional(),
  personalEmail: z.string().email().nullable().optional(),
  phone: z.string().min(7).nullable().optional(),
  homePhone: z.string().nullable().optional(),
  address: FacultyAddressSchema.nullable().optional(),
  employeeId: z.string().nullable().optional(),
  hireDate: UnixTimestamp.nullable().optional(),
  position: z.string().optional(),
  employmentType: FacultyEmploymentTypeSchema.nullable().optional(),
  divisions: z.array(z.string().min(1)).optional(),
  gradeLevels: z.array(z.string().min(1)).optional(),
  subjectLabels: z.array(z.string().min(1)).optional(),
  homeroom: z.string().nullable().optional(),
  reportsTo: z.string().nullable().optional(),
  tags: z.array(z.string().min(1)).optional(),
  userId: UserId.nullable().optional(),
  externalId: z.string().min(1).nullable().optional(),
  resourceId: ResourceId.nullable().optional(),
  primaryDivisionId: DivisionId.nullable().optional(),
  invitedAt: UnixTimestamp.nullable().optional(),
  activatedAt: UnixTimestamp.nullable().optional(),
});
export type UpdateFacultyInput = z.infer<typeof UpdateFacultyInputSchema>;
